import {
	Button,
	DateInput,
	Dialog,
	SelectFilter,
	TextField,
} from 'src/components';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
	Checkbox,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Grid,
	MenuItem,
	Paper,
} from '@material-ui/core';
import { type ICourse, type IUser, type IUserCourse } from 'src/models';
import SearchIcon from '@material-ui/icons/Search';
import VirtualizedTable from './VirtualizedTable';
import _ from 'lodash';
import moment from 'moment';

import 'moment/locale/es';
import { type RootState } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import courseActions from 'src/store/Courses/actions';
import useStyles from './UserCoursesDialog-styles';
import userActions from 'src/store/Users/actions';

interface Props {
	open: boolean;
	handleOpen: () => void;
	institutionId: number;
}

function UserCoursesDialog({ handleOpen, institutionId, open }: Props) {
	const classes = useStyles();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();
	const { institutionCourses, usersCourses } = useSelector(
		(state: RootState) => state.courses,
	);
	const users = useSelector(
		(state: RootState) => state.users.virtualizedUsers,
	);

	const getInstitutionCourses = (institutionId: number) =>
		dispatch(courseActions.getInstitutionCourses(institutionId));
	const getAllUsersByInstitution = (institutionId: number) =>
		dispatch(userActions.getAllByInstitution(institutionId));
	const getUsersCourses = (institutionId: number, courseId: number) =>
		dispatch(courseActions.getUsersCourses(institutionId, courseId));
	const saveUserCourses = (usersCourses: any[]) =>
		dispatch(courseActions.saveUsersCourses(usersCourses));

	const [parsedUsers, setParsedUsers] = useState<any[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [searchedParsedUsers, setSearchedParsedUsers] = useState<any[]>([]);
	const [courseId, setCourseId] = useState(0);
	const [expiresAt, setExpiresAt] = useState(new Date());
	const [search, setSearch] = useState('');

	function handleChangeCourse(event: ChangeEvent<HTMLInputElement>) {
		setCourseId(parseInt(event.target.value));
		getUsersCourses(institutionId, parseInt(event.target.value));
	}

	useEffect(() => {
		if (Boolean(institutionId) && open) {
			getInstitutionCourses(institutionId);
			getAllUsersByInstitution(institutionId);
		}
	}, [institutionId, open]);

	useEffect(() => {
		setParsedUsers(
			users.map((user: IUser) => ({
				department: user.department,
				email: user.email,
				expiresAt: (
					<DateInput
						disabled={
							!selectedUsers.find(
								(sU) => sU.userId === user.id && sU.active,
							)
						}
						name={'expiresAt'}
						onChange={(e: any) => {
							handleExpiresAtChange(
								user.id || '',
								e.target.value,
							);
						}}
						value={
							selectedUsers.find(
								(sU) => sU.userId === user.id && sU.active,
							)?.expiresAt || expiresAt
						}
					/>
				),
				id: user.id,
				name: `${user.firstname} ${user.lastname}`,
				selected: (
					<Checkbox
						checked={Boolean(
							selectedUsers.find(
								(sU) => sU.userId === user.id && sU.active,
							),
						)}
						color={'secondary'}
					/>
				),
			})),
		);
	}, [selectedUsers, users]);

	useEffect(() => {
		setSearchedParsedUsers(
			_.filter(parsedUsers, (user: any) =>
				user.name.toLowerCase().includes(search),
			) || parsedUsers,
		);
	}, [parsedUsers]);

	useEffect(() => {
		if (!open) {
			setCourseId(0);
			setSelectedUsers([]);
			setExpiresAt(new Date());
		}
	}, [open]);

	useEffect(() => {
		setSelectedUsers(
			usersCourses.map((userCourse: IUserCourse) => ({
				active: userCourse.active,
				courseId: userCourse.courseId,
				expiresAt: userCourse.expiresAt,
				userId: userCourse.userId,
			})),
		);
	}, [usersCourses]);

	useEffect(() => {
		setSearchedParsedUsers(
			_.filter(parsedUsers, (user: any) =>
				user.name.toLowerCase().includes(search),
			),
		);
	}, [search]);

	useEffect(() => {
		setSelectedUsers((selectedUsers: any[]) =>
			selectedUsers.map((selectedUser: IUserCourse) => ({
				...selectedUser,
				expiresAt,
			})),
		);
	}, [expiresAt]);

	function handleSelected(userId: string) {
		const _selectedUsers = [...selectedUsers];
		const index = _selectedUsers.findIndex((sE) => sE.userId === userId);

		if (index > -1)
			_selectedUsers[index].active = !_selectedUsers[index].active;
		else
			_selectedUsers.push({
				active: true,
				courseId,
				userId,
			});
		setSelectedUsers(_selectedUsers);
	}

	function handleExpiresAtChange(userId: string, expiresAt: Date) {
		const _selectedUsers = [...selectedUsers];
		const index = _selectedUsers.findIndex((sE) => sE.userId === userId);
		if (index > -1) _selectedUsers[index].expiresAt = expiresAt;
		setSelectedUsers(_selectedUsers);
	}

	const handleSelectAll = useCallback(
		(value: boolean) => {
			const _selectedUsers: any[] = [];

			if (value) {
				users.forEach((user: IUser) => {
					_selectedUsers.push({
						active: true,
						courseId,
						userId: user.id,
					});
				});

				setSelectedUsers(_selectedUsers);
			} else {
				setSelectedUsers(
					usersCourses.map((userCourse: IUserCourse) => ({
						active: false,
						courseId: userCourse.courseId,
						userId: userCourse.userId,
					})),
				);
			}
		},
		[users, usersCourses],
	);

	// NOTE:
	// ./src/modules/Private/Courses/Course.tsx[77:8]
	function handleSave() {
		saveUserCourses(
			selectedUsers.map((selectedUser: IUserCourse) => {
				if (selectedUser.active) return selectedUser;

				delete selectedUser.expiresAt;

				return selectedUser;
			}),
		);

		handleOpen();
	}

	return (
		<Dialog
			actions={
				<Button actionType={'save'} onClick={handleSave}>
					Guardar cambios
				</Button>
			}
			content={
				<Grid container>
					<Grid item xs={12}>
						<SelectFilter
							handleChange={handleChangeCourse}
							label={'Curso'}
							value={courseId}
						>
							<MenuItem value={0}> Selecciona un curso </MenuItem>

							{institutionCourses.map((course: ICourse) => (
								<MenuItem key={course.id} value={course.id}>
									{' '}
									{course.name}{' '}
								</MenuItem>
							))}
						</SelectFilter>
					</Grid>

					<Grid item style={{ marginTop: 24 }} xs={12}>
						{Boolean(courseId) && (
							<>
								<Grid
									alignItems={'center'}
									container
									justify={'space-between'}
									spacing={2}
									style={{ marginBottom: 8 }}
								>
									<Grid item xs={6}>
										<TextField
											InputProps={{
												endAdornment: <SearchIcon />,
											}}
											label={'Buscar'}
											onChange={(
												event: ChangeEvent<HTMLInputElement>,
											) => {
												setSearch(event.target.value);
											}}
											size={'small'}
											value={search}
										/>
									</Grid>

									<Grid
										className={classes.dateInputContainer}
										item
										md={4}
										xs={6}
									>
										<div>
											<FormLabel htmlFor={'expiresAt'}>
												{' '}
												Expiración{' '}
											</FormLabel>

											{moment(expiresAt).diff(
												new Date(),
											) > 0 ? (
												<FormHelperText>
													{' '}
													(
													{moment(
														expiresAt,
													).fromNow()}
													){' '}
												</FormHelperText>
											) : (
												<div />
											)}
										</div>

										<DateInput
											name={'expiresAt'}
											onChange={(e: any) => {
												setExpiresAt(e.target.value);
											}}
											value={expiresAt}
										/>
									</Grid>
								</Grid>

								<FormControlLabel
									control={
										<Checkbox
											checked={
												users.length ==
												selectedUsers.filter(
													(userCourse: IUserCourse) =>
														userCourse.active,
												).length
											}
											color={'secondary'}
											onChange={(
												event: ChangeEvent<HTMLInputElement>,
											) => {
												handleSelectAll(
													event.target.checked,
												);
											}}
										/>
									}
									label={'Seleccionar todo'}
								/>
							</>
						)}

						<Paper style={{ height: 500, width: '100%' }}>
							{Boolean(courseId) && (
								<VirtualizedTable
									columns={[
										{
											dataKey: 'selected',
											label: 'Seleccionado',
											width: 270,
										},
										{
											dataKey: 'name',
											label: 'Nombre',
											width: 600,
										},
										{
											dataKey: 'email',
											label: 'Email',
											width: 600,
										},
										{
											dataKey: 'department',
											label: 'Departamento',
											width: 600,
										},
										{
											dataKey: 'expiresAt',
											label: 'Expiración',
											width: 500,
										},
									]}
									onRowClick={({ index, rowData }: any) => {
										handleSelected(rowData.id);
									}}
									rowCount={searchedParsedUsers.length}
									rowGetter={({ index }: any) =>
										searchedParsedUsers[index]
									}
									users={searchedParsedUsers}
								/>
							)}
						</Paper>
					</Grid>
				</Grid>
			}
			handleOpen={handleOpen}
			maxWidth={'lg'}
			open={open}
			title={'Asignación de cursos'}
		/>
	);
}

export default UserCoursesDialog;
